import axios from "axios"
import errorHandler from "./errorHandler"
import { csrfToken, fetchCsrfToken } from "./csrf"
import { setTopologyId } from "@/services/logger"
import { TOPOLOGY_API } from "@/constants/topology"
import logger from "./logger"
import authStorage from "@/services/authStorage"
import {
  API_INTERCEPTOR_REQUEST,
  API_INTERCEPTOR_RESPONSE,
  API_INTERCEPTOR_ERROR,
} from "@/constants/events"

setTopologyId(TOPOLOGY_API)

// Track active requests for cancellation
const activeRequests = new Map()
// Hotfix: disable automatic dedupe cancellation to avoid cancel storms
// causing unhandled runtime overlays in current frontend flow.
const ENABLE_REQUEST_CANCELLATION = false
export const EDITOR_CLIENT_HEADER_NAME = "X-Editor-Client"
export const EDITOR_CLIENT_HEADER_VALUE = "Editor-SPA"

export function toSameOriginUrl(path) {
  if (!path) return path
  if (/^https?:\/\//i.test(path)) return path
  const normalizedPath = path.startsWith("/") ? path : `/${path}`
  if (typeof window !== "undefined" && window.location?.origin) {
    return `${window.location.origin}${normalizedPath}`
  }
  return normalizedPath
}

function getRuntimeHostname() {
  if (typeof window === "undefined") return ""
  return String(window.location?.hostname || "").toLowerCase()
}

function ensureTrailingSlash(url) {
  if (!url) return url
  return url.endsWith("/") ? url : `${url}/`
}

export function resolveEditorRuntimeMode(hostname = getRuntimeHostname()) {
  const normalizedHostname = String(hostname || "").toLowerCase()

  if (
    normalizedHostname === "localhost" ||
    normalizedHostname === "127.0.0.1" ||
    normalizedHostname === "host.docker.internal"
  ) {
    return "local"
  }

  if (normalizedHostname === "editor.dev.plastinka.com") {
    return "editor-dev"
  }

  if (normalizedHostname === "editor.plastinka.com") {
    return "editor-prod"
  }

  return "remote"
}

export function resolveApiBaseUrl(hostname = getRuntimeHostname()) {
  if (typeof window !== "undefined") {
    return "/api"
  }

  const runtimeMode = resolveEditorRuntimeMode(hostname)
  if (runtimeMode === "local") {
    return "/api"
  }

  return process.env.VUE_APP_API_URL || "/api"
}

export function resolveLegacyApiPrefix(hostname = getRuntimeHostname()) {
  const baseUrl = resolveApiBaseUrl(hostname)
  return baseUrl === "/api" ? "" : ensureTrailingSlash(baseUrl)
}

// LLM:METADATA
// :topology: [Domain:APILayer | Role:RequestCancellation | Pattern:Middleware]
// :relates-to: uses: "axios", required-by: "All Components"
// :rationale: "Add request cancellation support to prevent race conditions"
// :contract: pre: "API request initiated", post: "Request completed or cancelled", invariant: "No orphaned requests"
// :decision_cache: "Chose AbortController for standard request cancellation [RACE-003]"
// LLM:END
/**
 * Create request ID for tracking
 * @param {string} method - HTTP method
 * @param {string} url - Request URL
 * @returns {string} Request ID
 */
/**
 * Create request ID for tracking
 * @param {Object} config - Axios request config
 * @returns {string} Request ID
 */
function createRequestId(config) {
  const { method, url, data, params } = config
  let action = ""

  // Normalize URL: remove origin if it matches process.env.VUE_APP_API_URL or localhost:8080
  let normalizedUrl = url || ""
  if (normalizedUrl.startsWith("http")) {
    try {
      const urlObj = new URL(normalizedUrl)
      normalizedUrl = urlObj.pathname + urlObj.search
      // If it's a legacy /api/ prefix from absolute URL, keep it or normalize it
    } catch (e) {
      // Ignore parse error
    }
  }

  // Try to find "action" in body
  if (data) {
    if (typeof data === "string") {
      try {
        const parsedData = JSON.parse(data)
        action = parsedData.action || ""
      } catch (e) {
        // Not JSON - check if it's a URL-encoded string
        const match = data.match(/(^|[&?])action=([^&]+)/)
        if (match) action = decodeURIComponent(match[2])
      }
    } else if (data instanceof FormData) {
      action = data.get("action")
    } else if (typeof data === "object") {
      action = data.action || ""
    }
  }

  // Fallback: try to find "action" in params
  if (!action && params) {
    action = params.action || ""
  }

  return `${method}:${normalizedUrl}${action ? ":" + action : ""}`
}

/**
 * Normalize a transport request id to the bounded backend-safe correlation format.
 *
 * @precondition value is any caller-provided header value or nullish input.
 * @postcondition Returns the original string only when it already matches the safe request-id contract.
 * @mutates none
 */
export function normalizeRequestId(value) {
  const candidate = typeof value === "string" ? value.trim() : ""
  if (!candidate) {
    return null
  }

  return /^[a-z0-9][a-z0-9._:-]{7,127}$/i.test(candidate) ? candidate : null
}

/**
 * Create one backend-safe transport correlation id for the current HTTP request.
 *
 * @postcondition Returns a value matching /^[a-z0-9][a-z0-9._:-]{7,127}$/i.
 * @mutates none
 */
export function createTransportRequestId() {
  const timestamp = Date.now().toString(36)
  const randomPart =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID().replace(/-/g, "")
      : Math.random().toString(36).slice(2) +
        Math.random().toString(36).slice(2)

  return `req-${timestamp}-${randomPart}`.slice(0, 128)
}

function ensureHeaders(config) {
  if (!config.headers) {
    config.headers = {}
  }

  return config.headers
}

function getHeader(headers, name) {
  if (!headers) {
    return undefined
  }

  if (typeof headers.get === "function") {
    return headers.get(name)
  }

  return headers[name] ?? headers[name.toLowerCase()]
}

function setHeader(headers, name, value) {
  if (typeof headers.set === "function") {
    headers.set(name, value)
    return
  }

  headers[name] = value
}

/**
 * Apply truthful Editor SPA identity headers without overloading X-Requested-With.
 *
 * @precondition headers is an Axios-compatible mutable headers object.
 * @postcondition X-Requested-With keeps the XMLHttpRequest meaning; X-Editor-Client carries SPA identity.
 * @mutates headers
 */
export function applyEditorApiRequestHeaders(
  target,
  token = authStorage.getToken()
) {
  const isConfig =
    target &&
    typeof target === "object" &&
    Object.prototype.hasOwnProperty.call(target, "headers")
  const headers = isConfig ? target.headers || {} : target

  setHeader(headers, "X-Requested-With", "XMLHttpRequest")
  setHeader(headers, "X-Editor-Client", "Editor-SPA")

  if (token) {
    setHeader(headers, "Authorization", `Bearer ${token}`)
  }

  if (isConfig) {
    target.headers = headers
    return target
  }

  return headers
}

/**
 * Cancel active request
 * @param {string} requestId - Request ID to cancel
 */
function cancelRequest(requestId) {
  if (!ENABLE_REQUEST_CANCELLATION) return
  if (activeRequests.has(requestId)) {
    const controller = activeRequests.get(requestId)
    controller.abort()
    activeRequests.delete(requestId)
    logger.debug(API_INTERCEPTOR_RESPONSE, "Request cancelled", {
      requestId,
      reason: "New request initiated",
    })
  }
}

/**
 * Cancel all active requests
 */
export function cancelAllRequests() {
  if (!ENABLE_REQUEST_CANCELLATION) return
  activeRequests.forEach((controller, requestId) => {
    controller.abort()
    logger.debug(API_INTERCEPTOR_RESPONSE, "Request cancelled", {
      requestId,
      reason: "Component unmounting or cleanup",
    })
  })
  activeRequests.clear()
}

const api = axios.create({
  baseURL: resolveApiBaseUrl(),
  withCredentials: true, // Enable cross-origin cookies for session management
  timeout: 30000, // Prevent false-negative timeout errors on slow API responses
  headers: {
    "Content-Type": "application/json",
  },
})

// LLM:METADATA
// :topology: [Domain:API_Client | Role:Authentication_Middleware | Pattern:Interceptor]
// :relates-to: uses: "axios", required-by: "All Vue components"
// :rationale: "Automatically inject authentication token into all API requests to ensure consistent authorization."
// :contract: pre: "Request initiated", post: "Authorization header added if token exists", invariant: "Request config is returned"
// :decision_cache: "Chose request interceptor for automatic token injection [API-001]"
// LLM:END
api.interceptors.request.use(
  (config) => {
    const requestKey = createRequestId(config)
    const headers = ensureHeaders(config)
    const requestId =
      normalizeRequestId(getHeader(headers, "X-Request-ID")) ||
      createTransportRequestId()

    const isFormDataPayload =
      typeof FormData !== "undefined" && config.data instanceof FormData

    if (isFormDataPayload) {
      if (typeof headers.set === "function") {
        headers.set("Content-Type", undefined)
      } else {
        delete headers["Content-Type"]
        delete headers["content-type"]
      }
    }

    // Do NOT cancel auth/login/csrf — double-submit would kill the first request
    // Do NOT cancel dict — parallel fetches per module, navigation must not cancel in-flight
    const url = config.url || ""
    const isNoCancel = /auth|login|csrf|dict/i.test(url)
    if (ENABLE_REQUEST_CANCELLATION && !isNoCancel) {
      cancelRequest(requestKey)
    }

    // Respect caller-provided signal (e.g. list-level race guards in composables).
    // Otherwise create our own controller for optional global cancellation.
    if (!config.signal) {
      const controller = new AbortController()
      config.signal = controller.signal
      activeRequests.set(requestKey, controller)
    }

    setHeader(headers, "X-Request-ID", requestId)

    logger.debug(API_INTERCEPTOR_REQUEST, "Processing API request", {
      requestId,
      method: config.method,
      url: config.url,
      hasToken: !!authStorage.getToken(),
      hasCsrfToken: !!csrfToken.value,
    })

    // Identify requests from Editor SPA without overloading the XMLHttpRequest convention.
    applyEditorApiRequestHeaders(headers)

    const token = authStorage.getToken()
    if (token) {
      headers.Authorization = `Bearer ${token}`
    }

    // Add CSRF token to state-changing requests (fail-closed for protected writes)
    if (
      ["post", "put", "patch", "delete"].includes(config.method?.toLowerCase())
    ) {
      const requestPath = String(config.url || "")
      const csrfExempt =
        /auth\/(login|csrf-token|csrf_token|check|auth_token_check)/i.test(
          requestPath
        )

      if (!csrfExempt) {
        if (!csrfToken.value) {
          throw new Error("Missing CSRF token for mutating request")
        }
        headers["X-CSRF-Token"] = csrfToken.value
      }
    }

    return config
  },
  (error) => {
    logger.error(API_INTERCEPTOR_ERROR, "Request interceptor error", {
      error: error.message,
      stack: error.stack,
    })
    return Promise.reject(error)
  }
)

// LLM:METADATA
// :topology: [Domain:API_Client | Role:CSRF_Validation | Pattern:Interceptor]
// :relates-to: uses: "axios", required-by: "All Vue components"
// :rationale: "Validate and update CSRF token from response headers"
// :contract: pre: "Response received", post: "CSRF token updated if present", invariant: "Response is returned"
// :decision_cache: "Chose response interceptor for CSRF token refresh [API-003]"
// LLM:END
api.interceptors.response.use(
  (response) => {
    const requestKey = createRequestId(response.config)
    const requestId =
      normalizeRequestId(getHeader(response.config?.headers, "X-Request-ID")) ||
      createTransportRequestId()
    activeRequests.delete(requestKey)

    logger.debug(API_INTERCEPTOR_RESPONSE, "Processing API response", {
      requestId,
      status: response.status,
      hasCsrfToken: !!response.headers["x-csrf-token"],
    })

    // Validate CSRF token on response
    const newCsrfToken = response.headers["x-csrf-token"]
    if (newCsrfToken) {
      csrfToken.value = newCsrfToken
    }

    return response
  },
  (error) => {
    // LLM:METADATA
    // :topology: [Domain:APILayer | Role:ErrorHandling | Pattern:Interceptor]
    // :relates-to: uses: "errorHandler", required-by: "All Vue components"
    // :rationale: "Centralizes API error handling with logging and automatic redirect on 401/403"
    // :contract: pre: "API error received", post: "Error logged and handled", invariant: "Promise rejected for error propagation"
    // :decision_cache: "Chose error interceptor for centralized error handling [API-002]"
    // LLM:END
    const requestKey = error.config ? createRequestId(error.config) : "unknown"
    const requestId =
      normalizeRequestId(getHeader(error.config?.headers, "X-Request-ID")) ||
      (error.config ? createTransportRequestId() : "unknown")

    activeRequests.delete(requestKey)

    const isCanceled =
      axios.isCancel(error) ||
      error?.code === "ERR_CANCELED" ||
      error?.name === "AbortError" ||
      error?.message === "canceled"

    if (isCanceled) {
      logger.debug(API_INTERCEPTOR_RESPONSE, "Request canceled", {
        requestId,
        url: error.config?.url,
      })
      // Cancellation is expected in "latest request wins" flows.
      // Resolve with synthetic response to prevent unhandled promise noise.
      return Promise.resolve({
        data: null,
        status: 499,
        statusText: "Canceled",
        headers: {},
        config: error.config || {},
        __canceled: true,
      })
    }

    // DIAG: Always log — bypass logger in case NODE_ENV filters it
    console.error(
      `[API DIAG] Response error | ${requestId} | url:${error.config?.url} | error:${error.message}`
    )
    logger.error(API_INTERCEPTOR_ERROR, "Response error", {
      requestId,
      url: error.config?.url,
      status: error.response?.status,
      error: error.message,
      stack: error.stack,
    })

    // Handle explicit forbidden responses centrally so denied actions do not fail silently.
    if (error.response?.status === 403) {
      // CSRF token invalid, fetch new token; permission denials still keep the rejected promise for callers.
      fetchCsrfToken()
      error.__handledForbidden = true
      errorHandler.handle(error)
    }

    if (error.response && error.response.status === 401) {
      errorHandler.redirectToLogin()
      return Promise.resolve({
        data: null,
        status: 401,
        statusText: "Unauthorized",
        headers: error.response?.headers || {},
        config: error.config || {},
        __handled401: true,
      })
    }
    return Promise.reject(error)
  }
)

// LLM:METADATA
// :topology: [Domain:APILayer | Role:ErrorSuppression | Pattern:GlobalEventHandler]
// :relates-to: uses: "window.addEventListener", required-by: "Dev Runtime Overlay"
// :rationale: "Prevent dev overlay noise only for benign unhandled rejections (request cancellations and 401 redirects)."
// :contract: pre: "Unhandled promise rejection occurs", post: "Suppression only for cancel/401 cases", invariant: "Other errors surface to diagnostics"
// :decision_cache: "Chose global unhandledrejection patch to avoid per-call try/catch [SUP-001]"
// LLM:END
// Suppress noisy unhandled promise rejections only for expected cancellations and 401 redirects.
if (typeof window !== "undefined" && !window.__apiUnhandledRejectionPatched) {
  window.__apiUnhandledRejectionPatched = true
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event?.reason
    const isCanceled =
      reason?.code === "ERR_CANCELED" ||
      reason?.name === "AbortError" ||
      reason?.name === "CanceledError" ||
      reason?.message === "canceled"
    const isUnauthorized = reason?.response?.status === 401
    if (isCanceled || isUnauthorized) {
      event.preventDefault()
    }
  })
}

export default api
